import { Injectable, inject } from '@angular/core';
import { AuthService } from '@quipex/core/auth';
import type { ApiRequestOptions } from '@quipex/core/primitives';
import {
    qpxToast as toast,
    toProblemToastCopy,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import { mergeMap, throwError, type Observable } from 'rxjs';
import { toProblemDetails } from './models';

@Injectable({
    providedIn: 'root',
})
export class HandleErrorService {
    private readonly authService = inject(AuthService);

    public handleError(
        err: unknown,
        options?: ApiRequestOptions
    ): Observable<never> {
        return toProblemDetails(err).pipe(
            mergeMap((problemDetails) => {
                this.showErrorToast(problemDetails, options);

                // Always throw a ResolvedProblemDetails object
                return throwError(() => problemDetails);
            })
        );
    }

    private showErrorToast(
        problemDetails: ResolvedProblemDetails,
        options?: ApiRequestOptions
    ): void {
        const copy = toProblemToastCopy(problemDetails);

        // Status code 401 always shows the toast
        if (problemDetails.status === 401) {
            const handleUnauthorised = () => this.handleUnauthorized();

            toast.error(copy.message, {
                ...(copy.description ? { description: copy.description } : {}),
                onAutoClose: handleUnauthorised,
                onDismiss: handleUnauthorised,
            });
        }
        // Otherwise only show the toast if
        // `suppressAutoErrorToast` flag is false or undefined
        else if (options?.suppressAutoErrorToast !== true) {
            toast.error(copy.message, {
                ...(copy.description ? { description: copy.description } : {}),
            });
        }
    }

    /**
     * Clear local storage/session storage of any tokens or user data and logout user
     */
    private handleUnauthorized(): void {
        localStorage.clear();
        sessionStorage.clear();
        this.authService.logout();
    }
}
